import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "react-query";
import { useNavigate } from "react-router";
import { Box, Heading, Text } from "@chakra-ui/react";
import { useStiggContext } from "@stigg/react-sdk";
import Button from "../../components/Button";
import Input from "../../components/Input";
import Pagination from "../../components/Pagination";
import useSyncPagination from "../../components/Pagination/hooks/useSyncPagination";
import { Select } from "../../components/Select";
import Table from "../../components/Table";
import { TableComposite, TableDatum } from "../../components/Table/types";
import { PipelineImport } from "../../api/types";
import useGetOrganization from "../../api/useGetOrganization";
import useGetPipelineImports from "../../api/useGetPipelineImports";
import useGetSchemas from "../../api/useGetSchemas";
import useFilter from "../../hooks/useFilter";
import useLocalStorage from "../../hooks/useLocalStorage";
import useWebSocketMessage from "../../hooks/useWebSocketMessage";
import collectionCountLabel from "../../utils/collectionCountLabel";
import { typedSxMap } from "../../utils/typedSxMap";
import { tableRowNavigation } from "../../utils/navigation";
import UploadFileModal from "./components/UploadFileModal";
import { pipelineImportsTable } from "./utils/pipelineImportsTable";
import { PiMagnifyingGlassBold } from "react-icons/pi";

const styles = typedSxMap({
  importers: {
    flexGrow: 1,
    height: "100%",
  },
  container: {
    "&>:last-child": {
      marginTop: "var(--m-xs)",
      padding: 0,
    },
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "var(--m-xs)",
    marginBottom: "var(--m-xs)",
  },
  title: {
    "& h1": {
      marginBottom: 0,
    },
  },
  actions: {
    display: "flex",
    gap: "var(--m-xs)",
    "& .searchInput": {
      marginBottom: 0,
    },
  },
  searchInput: {},
  inputWrapper: {
    backgroundColor: "var(--color-input-background)",
    borderRadius: "var(--border-radius-2)",
    outline: "1px solid transparent",
    padding: "calc(var(--m-xxs) / 2)",
    border: "1px solid var(--color-border)",
    fontWeight: 400,
    display: "flex",
    alignItems: "center",
    minWidth: "270px",
    position: "relative",
  },
  inputIcon: {
    padding: "0 var(--m-xxxs)",
    cursor: "pointer",
    display: "flex",
    position: "relative",
  },
  info: {},
  textsPanel: {},
  footer: {},
  table: {
    display: "table",
    overflow: "auto",
  },
  tbody: {
    display: "table-row-group",
  },
  tr: {
    display: "table-row",
  },
  td: {
    display: "table-cell",
    verticalAlign: "middle",
    maxWidth: "300px",
    height: "48px",
  },
  tableName: {
    display: "flex",
    alignItems: "center",
    gap: "var(--m-xs)",
  },
  importerIcon: {
    color: "var(--color-primary)",
  },
  tableWrapper: {
    display: "flex",
    overflowY: "auto",
    padding: "1px",
    marginRight: "-20px",
    paddingRight: "21px",
    height: "auto",
    maxHeight: "calc(100vh - 126px)",
  },
  tableWrapperWithPagination: {
    maxHeight: "calc(100vh - 180px)",
  },
  dragContainer: {
    position: "relative",
  },
  dropZone: {
    background: "color-mix(in srgb, var(--color-primary) 20%, transparent)",
    position: "absolute",
    height: 0,
    width: "100%",
    top: 0,
    border: "2px dashed var(--color-text)",
    borderRadius: "var(--border-radius-2)",
    overflow: "hidden",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    opacity: 0,
  },
  schemaFilter: {
    borderRadius: "var(--border-radius-2)",
    "& button": {
      height: "29px",
      fontSize: "var(--font-size-1)",
    },
  },
  tableEmptyMessage: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
  },
});

export default function PipelineImports() {
  const navigate = useNavigate();
  const { data: organization } = useGetOrganization();
  const workspaceId = organization?.workspaces?.[0]?.id || "-";
  const [filterBySchemaId, setFilterBySchemaId] = useLocalStorage("filterBySchemaId", "-");
  const { isLoading, data: imports } = useGetPipelineImports(workspaceId, filterBySchemaId !== "-" ? filterBySchemaId : undefined);
  const { isLoading: isLoadingSchemas, data: schemas } = useGetSchemas(workspaceId);
  const { dataFiltered, setFilter } = useFilter<PipelineImport[]>(["name", "schema.name"], imports || []);
  const itemsPerPage = 25;
  const { dataPage, page, paginate, totalItems } = useSyncPagination(dataFiltered as any, itemsPerPage);
  const tableData = pipelineImportsTable(dataPage as any);
  const queryClient = useQueryClient();
  const message = useWebSocketMessage();
  const [isUploadFileModalOpen, setIsUploadFileModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const dropZoneRef = useRef<any>();
  const tableWrapperRef = useRef<any>();
  const fileInputRef = useRef<any>();
  const [schemaOptions, setSchemaOptions] = useState<any>({});
  const { stigg } = useStiggContext();
  const { hasAccess: hasExperimentationAccess } = stigg.getBooleanEntitlement({ featureId: "feature-experimentation" });

  useEffect(() => {
    const fetchData = async () => {
      if (message?.data === "refresh") {
        await queryClient.invalidateQueries(["pipeline-imports", workspaceId]);
      }
    };
    fetchData();
  }, [message, queryClient, workspaceId]);

  useEffect(() => {
    if (schemas?.length) {
      const schemasInputOptions: any = {};
      schemas.forEach(({ id, name }) => {
        schemasInputOptions[id as string] = { value: id, key: name, name };
      });
      setSchemaOptions(schemasInputOptions);
      const selectedSchema = schemas.find((schema) => schema.id === filterBySchemaId);
      setFilterBySchemaId(selectedSchema?.id || "-");
    }
  }, [schemas]);

  const handleClickedRow = (row: TableDatum) => {
    // Use the utility function with a conditional navigation check
    tableRowNavigation(
      row,
      navigate,
      "/import/${id}",
      (data) => {
        // Only navigate if the import status allows it
        const status = data.Status as TableComposite;
        return status?.raw === "complete" || status?.raw === "importing" || status?.raw === "review";
      }
    );
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleDragOver = () => {
    dropZoneRef.current.style.height = "100%";
    dropZoneRef.current.style.opacity = 1;
    tableWrapperRef.current.style.filter = "blur(5px)";
  };

  const hideDropZone = (event: any) => {
    event.preventDefault();
    dropZoneRef.current.style.height = "0";
    dropZoneRef.current.style.opacity = 0;
    tableWrapperRef.current.style.filter = "none";
  };

  const handleFileUpload = async (event: any) => {
    const file = event.target.files[0];
    const formData = new FormData();
    formData.append("file", file);
    setSelectedFile(file);
    setIsUploadFileModalOpen(true);
  };

  return (
    <Box sx={styles.importers}>
      <Box sx={styles.container}>
        <Box sx={styles.header}>
          <Box sx={styles.title}>
            <Heading as="h1">Imports</Heading>
            <small>
              {!isLoading && !isLoadingSchemas
                ? collectionCountLabel(
                    "No imports",
                    "import",
                    "imports",
                    imports?.length || 0,
                    "No",
                    `import${dataFiltered.length === 1 ? "" : "s"} found from`,
                    dataFiltered.length
                  )
                : "No imports"}
            </small>
          </Box>

          <Box sx={styles.actions}>
            <Box sx={styles.schemaFilter}>
              <Select
                options={{
                  All: {
                    key: "All",
                    name: "All",
                    value: "-",
                  },
                  ...schemaOptions,
                }}
                name="schema"
                value={filterBySchemaId || ""}
                onChange={(selectedSchema: any) => {
                  selectedSchema && setFilterBySchemaId((selectedSchema as any) || "");
                }}
                disabled={isLoading}
              />
            </Box>
            <Input
              className="searchInput"
              icon={<PiMagnifyingGlassBold />}
              type="search"
              autoComplete="off"
              placeholder="Search"
              onChange={(e: any) => setFilter(e.target.value)}
            />
            <input
              type="file"
              accept={hasExperimentationAccess ? ".csv,.xlsx,.pdf" : ".csv,.xlsx"}
              style={{ display: "none" }}
              ref={fileInputRef}
              onChange={handleFileUpload}
            />
            <Button variants={["primary"]} onClick={handleUploadClick} tabIndex={-1}>
              Upload
            </Button>
          </Box>
        </Box>

        <Box sx={styles.dragContainer}>
          <Box
            ref={tableWrapperRef}
            sx={{ ...styles.tableWrapper, ...(!!(totalItems && totalItems > itemsPerPage) ? styles.tableWrapperWithPagination : {}) }}
            onDragEnd={hideDropZone}
            onDragOver={handleDragOver}>
            {imports && (
              <Table
                onRowClick={handleClickedRow}
                data={tableData as any}
                emptyState={<Box>No imports found</Box>}
                emptyMessageStyles={{ width: "100%" }}
                /* heading={<Heading {...{ setSort, sortKey, sortAsc }} />} */
                columnWidths={["24%", "24%", "13%", "8%", "16%", "15%"]}
                // columnWidths={["24%", "24%", "15%", "10%", "11%", "11%", "5%"]}
                getRowStyles={(datum: any) => ({
                  cursor:
                    (datum.Status as any)?.raw === "review" || (datum.Status as any)?.raw == "importing" || (datum.Status as any)?.raw == "complete"
                      ? "pointer"
                      : "auto",
                })}
              />
            )}
          </Box>
          <Box
            ref={dropZoneRef}
            sx={styles.dropZone}
            onDragOver={(event) => {
              event.preventDefault();
            }}
            draggable
            onDragLeave={hideDropZone}
            onDrop={(event) => {
              event.preventDefault();
              fileInputRef.current.files = event.dataTransfer.files;
              setSelectedFile(fileInputRef.current.files[0]);
              setIsUploadFileModalOpen(true);
              hideDropZone(event);
            }}>
            <Text>Drop file here</Text>
          </Box>
        </Box>

        {!!(totalItems && totalItems > itemsPerPage) && (
          <Pagination totalItems={totalItems} itemsPerPage={itemsPerPage} onPageChange={paginate} initialPage={page} />
        )}
      </Box>
      <UploadFileModal
        schemaOptions={schemaOptions}
        isOpen={isUploadFileModalOpen}
        workspaceId={workspaceId}
        selectedFile={selectedFile}
        onClose={() => {
          setIsUploadFileModalOpen(false);
          setSelectedFile(null);
          fileInputRef.current.value = "";
        }}></UploadFileModal>
    </Box>
  );
}
